import React, { useEffect } from "react"
import { Box, Button, DialogContent, DialogTitle, Divider, Typography, useTheme } from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl } from "react-intl"
import {
  DialogActionsBar,
  Form,
  FormOnSubmitHandler,
  FormRadioGroup,
  FormTextField
} from "@carrotfertility/carotene-core-x"
import { useCarrotMatch } from "#/components/carrot-match-fertility-clinic-precision-matching/context/CarrotMatchContext"
import {
  Feedback,
  Questions,
  User
} from "#/components/carrot-match-fertility-clinic-precision-matching/utils/carrotMatchTypes"
import { Provider } from "#/pages/provider-finder/types/Providers"
import { CarrotMatchGeneralCloseButton } from "#/components/carrot-match-fertility-clinic-precision-matching/steps/CarrotMatchGeneralCloseButton"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineV2"

function ProviderFeedback({ provider }: { provider: Provider }): JSX.Element {
  const { providerUuid, providerName } = provider
  const intl = useIntl()
  const theme = useTheme()

  const options: { label: string; value: Feedback }[] = [
    {
      label: intl.formatMessage({ defaultMessage: "Unlikely" }),
      value: Feedback.UNLIKELY
    },
    {
      label: intl.formatMessage({ defaultMessage: "Not sure" }),
      value: Feedback.NOT_SURE
    },
    {
      label: intl.formatMessage({ defaultMessage: "Likely" }),
      value: Feedback.LIKELY
    }
  ]

  return (
    <Box>
      <Typography variant="body1" color="textSecondary" sx={{ marginBlockEnd: theme.spacing(theme.tokens.spacing.md) }}>
        {providerName}
      </Typography>
      <FormRadioGroup
        options={options}
        name={`${providerUuid}`}
        radioGroupProps={{
          row: true,
          sx: {
            ">*:not(:first-of-type)": {
              marginInlineStart: theme.spacing(theme.tokens.spacing.md)
            }
          }
        }}
        sx={{
          marginBlockEnd: theme.spacing(theme.tokens.spacing.sm)
        }}
        controllerProps={{
          rules: {
            required: "Required"
          }
        }}
      />
      <Divider />
    </Box>
  )
}

export function MatchResultsFeedbackStep(): JSX.Element {
  const { carrotMatchInteraction, updateCarrotMatchInteraction } = useCarrotMatch()
  const {
    memberPreferredNameOrFirstName,
    partnerPreferredNameOrFirstName,
    whoIsAnsweringTheIntake,
    whoIsSeekingCare,
    matchedProviders
  } = carrotMatchInteraction
  const intl = useIntl()
  const theme = useTheme()
  const { send } = useStateMachine()

  useEffect(() => {
    updateCarrotMatchInteraction({ lastQuestionViewedBeforeLeavingFlow: Questions.WHAT_MATTERS_TO_XYZ_QUESTION })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- See https://carrotfertility.atlassian.net/wiki/spaces/PE/pages/2050295461/Remove+Build+Warnings#react-hooks%2Fexhaustive-deps
  }, [])

  const getFeedbackDialogTitle = () => {
    if (whoIsAnsweringTheIntake === whoIsSeekingCare) {
      return intl.formatMessage({ defaultMessage: "How likely are you to seek care at the recommended clinics? " })
    } else if (whoIsSeekingCare === User.PARTNER) {
      return intl.formatMessage(
        { defaultMessage: "How likely is {partnerPreferredNameOrFirstName} to seek care at the recommended clinics?" },
        { partnerPreferredNameOrFirstName }
      )
    } else {
      return intl.formatMessage(
        { defaultMessage: "How likely is {memberPreferredNameOrFirstName} to seek care at the recommended clinics?" },
        { memberPreferredNameOrFirstName }
      )
    }
  }

  const handleShareFeedback: FormOnSubmitHandler = async (data) => {
    const { additionalFeedback, ...providerFeedback } = data

    updateCarrotMatchInteraction({
      matchFeedback: providerFeedback,
      additionalFeedback: additionalFeedback
    })

    send("")
  }

  return (
    <>
      <Form onSubmit={handleShareFeedback}>
        <CarrotMatchGeneralCloseButton />
        <Box sx={{ marginBlockEnd: theme.spacing(theme.tokens.spacing.xxl - theme.tokens.spacing.sm) }}>
          <DialogTitle id="step-heading">{getFeedbackDialogTitle()}</DialogTitle>
        </Box>
        <DialogContent>
          {matchedProviders.map((provider) => (
            <ProviderFeedback provider={provider} key={provider.providerUuid} />
          ))}
          <FormTextField
            name="additionalFeedback"
            multiline
            minRows={3}
            label={intl.formatMessage({ defaultMessage: "Any additional feedback on these recommendations?" })}
          />
        </DialogContent>
        <DialogActionsBar
          primaryAction={
            <Button color="primary" type="submit">
              <FormattedMessage defaultMessage="Share feedback" />
            </Button>
          }
        />
      </Form>
    </>
  )
}
